import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { PayloadAction as RTKPayloadAction } from '@reduxjs/toolkit';
import { type VariantStock, type SearchVariant, productSearchApi, SearchProduct } from '../Services/productSearchApi';
import { cartApi } from '../Services/cartApi';
import { IShoppingCartModel, IAlphaMiniCartModel } from "../Types/Litium";

export interface CartVariantData {
    baseId: string;
    id: string;
    size: string;
    color: string;
    colorId: string;
    colorName: string;
    stock: CartVariantDataStock;
}

interface CartVariantDataStock extends VariantStock {
    isOutOfStock: boolean;
}

interface RowSpecificSearchResult {
    variants: CartVariantData[];
    selected: string;
    rowId: string;
}

interface RowSpecificVariantSelects {
    [rowId: string]: RowSpecificVariantSelect;
}

export interface RowSpecificVariantSelect {
    selectedColor: VariantSelectOption;
    selectedSize: VariantSelectOption;
    colors: VariantSelectOption[];
    sizes: VariantSelectOption[];
    purchaseOrders: VariantPurchaseOrders[];
    quantity: number;
    overbooked: boolean;
}

interface VariantPurchaseOrders {
    deliveryDate: string;
    quantityInPurchaseOrder: number;
}

interface VariantSelectOption {
    label: string;
    value: string;
    outOfStock: boolean;
    quantity: number;
}

export const initialState = {
    variants: {},
    loaded: false
};

const slice = createSlice({
    name: 'variantData',
    initialState,
    reducers: {
        addVariantData(state, action: PayloadAction<SearchVariant[]>) {
            appendVariantSearchResultToLocalStorage(action.payload);
        }
    },
    extraReducers: builder => {
        builder.addMatcher(
            cartApi.endpoints.getCart.matchFulfilled,
            (state, action: RTKPayloadAction<any>) => {
                if (action.payload.miniCartModel.shoppingCart) {
                    Object.assign(state, getVariantDataBasedOnCart(action.payload.miniCartModel.shoppingCart));
                }
            }
        );
        builder.addMatcher(
            cartApi.endpoints.addToCart.matchFulfilled,
            (state, action: RTKPayloadAction<any>) => {
                if (action.payload.miniCartModel.shoppingCart) {
                    Object.assign(state, getVariantDataBasedOnCart(action.payload.miniCartModel.shoppingCart));
                }
            }
        );
        builder.addMatcher(
            cartApi.endpoints.updateCartRow.matchFulfilled,
            (state, action: RTKPayloadAction<any>) => {
                if (action.payload.miniCartModel.shoppingCart) {
                    Object.assign(state, getVariantDataBasedOnCart(action.payload.miniCartModel.shoppingCart));
                }
            }
        );
        builder.addMatcher(
            cartApi.endpoints.replaceCart.matchFulfilled,
            (state, action: RTKPayloadAction<any>) => {
                if (action.payload.miniCartModel.shoppingCart) {
                    Object.assign(state, getVariantDataBasedOnCart(action.payload.miniCartModel.shoppingCart));
                }
            }
        );
        builder.addMatcher(
            cartApi.endpoints.loadOrderIntoCart.matchFulfilled,
            (state, action: RTKPayloadAction<any>) => {
                if (action.payload.gudrunCartVariantData) {
                    appendProductSearchResultToLocalStorage(action.payload.gudrunCartVariantData);
                }
                if (action.payload.miniCartModel.shoppingCart) {
                    Object.assign(state, getVariantDataBasedOnCart(action.payload.miniCartModel.shoppingCart));
                }
            }
        );
    },
});

export const { addVariantData } = slice.actions;

export default slice.reducer;

function appendProductSearchResultToLocalStorage(productSearchResult: SearchProduct) {
    const storedData = localStorage.getItem('gudrunCartVariantData');
    const variantData = Object.entries(productSearchResult).reduce((acc, item) => {
        acc[item[0]] = item[1].map((v) => mapVariantSearchResultToLocalStorageData(v));
        return acc;
    }, {});

    const newData = {
        ...(storedData ? JSON.parse(storedData) : {}),
        ...variantData,
    };

    localStorage.setItem('gudrunCartVariantData', JSON.stringify(newData));
}

function appendVariantSearchResultToLocalStorage(variantSearchResult: SearchVariant[]) {
    const storedData = localStorage.getItem('gudrunCartVariantData');
    const variantData = variantSearchResult.map(k =>
        mapVariantSearchResultToLocalStorageData(k)
    );

    const newData = {
        ...(storedData ? JSON.parse(storedData) : {}),
        [variantData[0].baseId]: variantData,
    };

    localStorage.setItem('gudrunCartVariantData', JSON.stringify(newData));
}

function getVariantDataBasedOnCart(shoppingCart: IShoppingCartModel) {
    if (!shoppingCart) {
        return { variants: {}, loaded: false };
    }

    const storedData = localStorage.getItem('gudrunCartVariantData');
    if (!storedData || !shoppingCart?.orderItems?.length) return { variants: {}, loaded: true };

    const data = JSON.parse(storedData);
    const cartBasedData = shoppingCart.orderItems.map(o => {
        const rowData = data[o.baseId as keyof RowSpecificVariantSelects];

        return {
            variants: rowData,
            selected: o.id,
            rowId: o.systemId as keyof RowSpecificVariantSelects,
        } as RowSpecificSearchResult;
    });

    localStorage.setItem('gudrunCartVariantData', JSON.stringify(shoppingCart.orderItems.reduce((acc, item) => {
        acc[item.baseId] = data[item.baseId as keyof RowSpecificVariantSelects];
        return acc;
    }, {} as RowSpecificVariantSelects)));

    return {
        variants: mapLocalStorageDataToVariantData(cartBasedData),
        loaded: true 
    };
};

function mapLocalStorageDataToVariantData(
    data: RowSpecificSearchResult[]
): RowSpecificVariantSelects {    
    return data.reduce((acc, { variants, selected, rowId }) => {
        const selectedVariant =
            variants.find(v => v.id === selected) ||
            variants.find(v => !isOutOfStock(v.stock));

        if (!selectedVariant) {
            return acc;
        }

        const sizeToColor = new Map<string, Set<string>>();
        const colorToSize = new Map<string, Set<string>>();
        const colorAvailability = new Map<
            string,
            { id: string; stock: number }
        >();

        const calculateStock = (stock: CartVariantDataStock) =>
            (stock as any).quantityInStock +
            (stock as any).overbookedQuantity +
            (stock as any).purchaseOrders.reduce(
                (acc: number, curr: any) => acc + curr.quantityInPurchaseOrder,
                0
            );

        const isOutOfStock = (stock: CartVariantDataStock) =>
            calculateStock(stock) <= 0;

        variants.forEach(({ color, size, id, stock }) => {
            if (!sizeToColor.has(size)) sizeToColor.set(size, new Set());
            sizeToColor.get(size)!.add(color);

            if (!colorToSize.has(color)) colorToSize.set(color, new Set());
            colorToSize.get(color)!.add(size);

            if (!isOutOfStock(stock) && !colorAvailability.has(color)) {
                colorAvailability.set(color, {
                        id,
                        stock: calculateStock(stock),
                    });
            }
        });
        
        const sizes = Array.from(sizeToColor.keys()).map(size => {
            const variant = variants.find(
                v => v.size === size && v.color === selectedVariant.color
            );
            let variantId = variant?.id || '';
            let variantStock = variant ? calculateStock(variant.stock) : 0;
            
            return {
                value: variantId,
                label: size,
                outOfStock: isOutOfStock(variant.stock),
                quantity: variantStock,
            };
        });

        const colors = Array.from(colorToSize.keys()).map(color => {
            const variant = variants.find(
                v => v.color === color && v.size === selectedVariant.size
            );
            let variantId = variant?.id || '';
            let variantStock = variant ? calculateStock(variant.stock) : 0;

            if (!variant || isOutOfStock(variant.stock)) {
                const availableVariant = colorAvailability.get(color);
                if (availableVariant) {
                    variantId = availableVariant.id;
                    variantStock = availableVariant.stock;
                }
            }

            const colorVariant = variants.find(v => v.color === color);
            return {
                value: variantId,
                label: `(${colorVariant?.colorId || ''}) ${colorVariant?.colorName || ''}`,
                outOfStock: !colorAvailability.get(color),
                quantity: variantStock,
            };
        });

        acc[rowId] = {
            selectedColor: {
                value: selectedVariant.id,
                label: `(${selectedVariant.colorId || ''}) ${selectedVariant.colorName || ''}`,
                outOfStock: !colorAvailability.get(selectedVariant.color),
                quantity: calculateStock(selectedVariant.stock),
            },
            selectedSize: {
                value: selectedVariant.id,
                label: selectedVariant.size,
                outOfStock: isOutOfStock(selectedVariant.stock),
                quantity: calculateStock(selectedVariant.stock),
            },
            colors,
            sizes,
            quantity: (selectedVariant.stock as any).quantityInStock,
            purchaseOrders: (selectedVariant.stock as any).purchaseOrders || [],
            overbooked: (selectedVariant.stock as any).overbookedQuantity > 0,
        };

        return acc;
    }, {});
}

export function isOutOfStock(stock: VariantStock) {
    return stock.quantityInStock === 0 && !stock.purchaseOrders.length;
}

function mapVariantSearchResultToLocalStorageData(variant: SearchVariant): CartVariantData {
    return {
        baseId: variant.baseProductId,
        id: variant.variantId,
        size: variant.size,
        color: variant.colorHex,
        colorName: variant.colorName,
        colorId: variant.colorId,
        stock: {
            ...variant.stock,
            isOutOfStock: isOutOfStock(variant.stock),
        },
    };
}
